import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';

interface NavChild {
  name: string;
  path: string;
}

interface NavItem {
  name: string;
  path?: string;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  {
    name: 'About',
    children: [
      { name: 'Our Story', path: '/about' },
      { name: 'Certifications', path: '/about#certifications' },
      { name: 'Our Team', path: '/team' },
    ],
  },
  { name: 'Track Record', path: '/track-record' },
  {
    name: 'Products',
    children: [
      { name: 'All Products', path: '/products' },
      { name: 'Rice', path: '/products#rice' },
      { name: 'Beans & Peas', path: '/products#beans' },
      { name: 'Maize', path: '/products#maize' },
      { name: 'Groundnuts', path: '/products#groundnuts' },
      { name: 'Sugar', path: '/products#sugar' },
    ],
  },
  {
    name: 'Services',
    children: [
      { name: 'Overview', path: '/services' },
      { name: 'Logistics & Transport', path: '/services#logistics' },
      { name: 'Documentation', path: '/services#documentation' },
      { name: 'Custom Grading', path: '/services/custom-grading' },
      { name: 'Grain Specifications', path: '/services/grain-specifications' },
    ],
  },
  {
    name: 'Suppliers Circle',
    children: [
      { name: 'Become a Supplier', path: '/suppliers/become-a-supplier' },
    ],
  },
  { name: 'Contact', path: '/contact' },
];

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActiveParent = (item: NavItem) => {
    if (item.path) return location.pathname === item.path;
    if (item.children) {
      return item.children.some((child) =>
        location.pathname.startsWith(child.path.split('#')[0]) &&
        child.path.split('#')[0] !== '/'
      );
    }
    return false;
  };

  return (
    <motion.nav
      ref={navRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-cream-50/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="/icons/revianicon.png"
              alt="Revian Rice Stores"
              className="w-40 h-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-5">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.name} className="relative">
                  <button
                    onMouseEnter={() => setOpenDropdown(item.name)}
                    onClick={() =>
                      setOpenDropdown(openDropdown === item.name ? null : item.name)
                    }
                    className={`flex items-center gap-1 font-inter font-medium text-sm transition-colors duration-200 ${
                      isActiveParent(item)
                        ? 'text-primary-red'
                        : 'text-primary-black hover:text-primary-red'
                    }`}
                  >
                    {item.name}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${
                        openDropdown === item.name ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {openDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.18 }}
                        className="absolute top-full left-0 mt-2 min-w-[210px] bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50"
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={`block px-4 py-2.5 text-sm font-inter transition-colors duration-150 ${
                              location.pathname === child.path.split('#')[0] &&
                              child.path.split('#')[0] !== '/'
                                ? 'text-primary-red bg-primary-red/5'
                                : 'text-gray-700 hover:text-primary-red hover:bg-gray-50'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.path}
                  to={item.path!}
                  className={`font-inter font-medium text-sm transition-colors duration-200 ${
                    location.pathname === item.path
                      ? 'text-primary-red'
                      : 'text-primary-black hover:text-primary-red'
                  }`}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-primary-black hover:text-primary-red transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-cream-50 border-t border-gray-200 overflow-hidden"
          >
            <div className="container-custom py-4 space-y-1">
              {navItems.map((item, index) =>
                item.children ? (
                  <div key={item.name}>
                    <motion.button
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.04 }}
                      onClick={() =>
                        setMobileExpanded(
                          mobileExpanded === item.name ? null : item.name
                        )
                      }
                      className={`w-full flex items-center justify-between py-3 font-inter font-medium transition-colors duration-200 ${
                        isActiveParent(item) ? 'text-primary-red' : 'text-primary-black'
                      }`}
                    >
                      {item.name}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          mobileExpanded === item.name ? 'rotate-180' : ''
                        }`}
                      />
                    </motion.button>
                    <AnimatePresence>
                      {mobileExpanded === item.name && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="pl-4 space-y-1 pb-2 border-l-2 border-gray-100 ml-2"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`block py-2 text-sm font-inter transition-colors duration-150 ${
                                location.pathname === child.path.split('#')[0] &&
                                child.path.split('#')[0] !== '/'
                                  ? 'text-primary-red'
                                  : 'text-gray-600 hover:text-primary-red'
                              }`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.04 }}
                  >
                    <Link
                      to={item.path!}
                      className={`block py-3 font-inter font-medium transition-colors duration-200 ${
                        location.pathname === item.path
                          ? 'text-primary-red'
                          : 'text-primary-black hover:text-primary-red'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
